import { useCallback, useEffect, useRef, useState } from 'react';

import InlineSvg from 'approot/shared/inline-svg/inline-svg';
import { SecondaryButtonIcon } from 'approot/shared/styles/buttons/secondary-button.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import IconAudioOff from 'approot/shared/icons/icon-audio-off';

type Props = {
  audioUrl: string | null;
  description: string | null;
};

const FileDropperAudioButton = ({ audioUrl, description }: Props) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(new Audio(audioUrl || ''));

  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;

    const audioInstance = audioRef.current;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleIsReady = () => setIsReady(true);

    audioInstance.addEventListener('play', handlePlay);
    audioInstance.addEventListener('pause', handlePause);
    audioInstance.addEventListener('ended', handleEnded);
    audioInstance.addEventListener('canplaythrough', handleIsReady);

    return () => {
      audioInstance.pause();
      audioInstance.removeEventListener('play', handlePlay);
      audioInstance.removeEventListener('pause', handlePause);
      audioInstance.removeEventListener('ended', handleEnded);
      audioInstance.removeEventListener('canplaythrough', handleIsReady);
    };
  }, [audioUrl]);

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !isReady) return;

    try {
      if (audio.paused) {
        audio.play();
      } else {
        audio.pause();
      }
    } catch {
      // Do nothing
    }
  }, [isReady]);

  return (
    <TooltipWrapper hoverText="Play audio">
      <SecondaryButtonIcon
        type="button"
        $borderRadius={40}
        $noBorder
        aria-label={
          isPlaying
            ? `Pause audio for ${description}`
            : `Play audio for ${description}`
        }
        onClick={(e: any) => {
          e.stopPropagation();
          togglePlayback();
        }}
      >
        {isPlaying ? (
          <IconAudioOff width={20} height={20} />
        ) : (
          <InlineSvg id="audio" width={20} height={20} />
        )}
      </SecondaryButtonIcon>
    </TooltipWrapper>
  );
};

export default FileDropperAudioButton;
